import { useRef } from 'react'
import {
  fetchMe, patchProfile, addPlate, removePlate, fetchPaymentMethod, listPaymentMethods, setDefaultPaymentMethod, removePaymentMethod, setCardLabel, createBillingSession,
  fetchSubscriptions, fetchPermits, setAutoRenew, extendSession, extendQuote, fetchReceipt,
  emailReceipt, getEmail, startPhoneChange, verifyPhoneChange, setToken,
  sendCustomerChat, fileRefundRequest,
} from '../api'
import { AccountExperience, type AccountClient } from '@parkify/account'

/**
 * The portal's signed-in account view. The whole account experience — profile,
 * plates, live session, session history + receipts, saved-card management, the
 * Permits tab, and communication preferences — now lives in the shared
 * @parkify/account package (also rendered by the consumer checkout). This thin
 * wrapper binds the portal's api.ts functions into the package's AccountClient
 * and wires the portal's hash-routing + the Vite billing-portal flag, so the
 * package itself stays host-agnostic.
 */

// The post-booking SMS short link (`pay.parkify.com/r/{code}`) lands the parker
// here at `/?session={txnId}` so the shared experience can focus that row. Read
// from the portal's URL here and handed to the package as focusSessionId.
function focusSessionFromUrl(): string | null {
  try {
    const fromSearch = new URLSearchParams(window.location.search).get('session')
    if (fromSearch) return fromSearch
    const hash = window.location.hash.replace(/^#/, '')
    const qIndex = hash.indexOf('?')
    if (qIndex >= 0) return new URLSearchParams(hash.slice(qIndex + 1)).get('session')
  } catch { /* ignore malformed URLs */ }
  return null
}

export function Home({ onSignOut }: { onSignOut: () => void }) {
  // Captured once on mount, mirroring the previous in-screen behaviour.
  const focusSessionId = useRef(focusSessionFromUrl()).current

  const portalClient: AccountClient = {
    fetchMe, patchProfile, addPlate, removePlate, fetchPaymentMethod, listPaymentMethods, setDefaultPaymentMethod, removePaymentMethod, setCardLabel, createBillingSession,
    fetchSubscriptions, fetchPermits, setAutoRenew, extendSession, extendQuote, fetchReceipt,
    emailReceipt, getEmail, startPhoneChange, verifyPhoneChange,
    sendCustomerChat, fileRefundRequest,
  }

  return (
    <AccountExperience
      client={portalClient}
      billingPortalEnabled={import.meta.env.VITE_ENABLE_BILLING_PORTAL === 'true'}
      onFindParking={() => { window.location.hash = '/find' }}
      onSignInAgain={() => { window.location.hash = '/' }}
      onSignOut={onSignOut}
      onSessionRefreshed={setToken}
      focusSessionId={focusSessionId}
    />
  )
}
